import React from 'react'
import type * as File from '../../../shared/domain/files/types.no-deps'
import type { SyncGuide, ReportGuideId } from '../../../shared/domain/guides'
import type { ParentInfo, WorkflowAppGuide } from '../../../app-core/lib/guide/Model'
import * as LaunchDarkly from '../Util/Util.launchdarkly'
import { ZapBotModal } from '../../../app-core/lib/zapBot/ZapBotModal'
import { Flow_renderExecutionPage, fetchGuideReportId, fetchReport } from '../Flow/Flow.ui.ExecutionPage'
import { Rout_back } from '../Rout/Rout'
import { Imag_renderCachedImage } from '../Imag/Imag.ui.CachedImage'
import { renderPromise } from '../../../app-core/lib/util/PromiseRenderer'
import { DS_getData } from '../DS/DS.Data'
import type { Report } from '../../../app-core/lib/flow/ExecutionPage'
import { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import {
    Guid_downloadFile,
    Guid_fetchAppGuides,
    Guid_fetchFileGuideDetailsMap,
    Guid_fetchGuideParentInfo,
    Guid_fetchReportGuideDetails,
    Guid_fetchSyncGuidesByIds,
    Guid_routeToGuideParentPage,
} from '../Guid/Guid.helpers'
import {
    type AiChatSource,
    type AiChatUserContext,
    type ChatId,
    type AiChatGuideSource,
    isAiChatGuideSource,
} from '../../../shared/domain/aiChats/types.no-deps'
import type { Nullable } from '../../../shared/lib/typeHelpers.no-deps'
import { route } from '../../../server/http/aiChats/addPrompt.no-deps'
import { Serv_baseHttpRequestWithFetchAPI } from '../Serv/Serv.httpRequest'
import { Rout_toReportView } from '../Rout/Rout.report'
import { Base_commonDeps } from '../Base/Base.commonDeps'
import { Guides_sync } from '../Guid/Guid.sync'
import { doesUserHavePermission } from '../User/permissions'
import { ApiClient } from '../../../app-core/lib/util/ApiClient'
import { Cust_httpUrlGet } from '../Cust/Cust.get'

const httpReqWithFetchAPI = Serv_baseHttpRequestWithFetchAPI({})

export function Glbl_ZapBot() {
    const { accessToken, customer } = DS_getData()
    const [isZapBotEnabled, setIsZapBotEnabled] = React.useState(false)
    const [isZapBotFilesEnabled, setIsZapBotFilesEnabled] = React.useState(false)
    const [isAiCreditsEnabled, setIsAiCreditsEnabled] = React.useState(false)
    const [showZapBotModal, setShowZapBotModal] = React.useState(false)

    React.useEffect(() => {
        const setZapBotEnabledFlag = async () => {
            const isZapBotEnabled = LaunchDarkly.getFlagValue('ai-zap-bot', false)
            const isZapBotFilesEnabled = LaunchDarkly.getFlagValue('ai-zap-bot-files', false)
            const isAiCreditsEnabled = LaunchDarkly.getFlagValue('ai-credits', false)

            setIsZapBotEnabled(isZapBotEnabled)
            setIsZapBotFilesEnabled(isZapBotFilesEnabled)
            setIsAiCreditsEnabled(isAiCreditsEnabled)
        }

        setZapBotEnabledFlag()
    }, [])

    const user = DS_getData().user
    const canRenderZapBot = user && doesUserHavePermission(user, 'aiFunctionality') && isZapBotEnabled
    if (!canRenderZapBot) {
        return null
    }

    const deps = Base_commonDeps()
    const logger = new ClientLogger({ logGroupName: `Glbl_ZapBot` })
    const apiClient = new ApiClient(Cust_httpUrlGet(customer), accessToken, deps.customerSlug)

    const handleOnClose = () => setShowZapBotModal(false)
    const handleOnClickAiButton = () => setShowZapBotModal(true)
    const handleOnClickSource = async (source: AiChatSource) => {
        if (source.type === 'report') {
            Rout_toReportView(source.report.id)
            handleOnClose()

            return true
        }

        return false
    }

    const fetchFileGuideDetails = (id: File.FileId) =>
        Guid_fetchFileGuideDetailsMap(deps, [id]).then((urlMap) => urlMap.get(id))

    const fetchAppGuides = (syncGuides: SyncGuide[], withParentInfo: boolean) =>
        Guid_fetchAppGuides(deps, logger, syncGuides, withParentInfo)

    const fetchReportGuideDetails = (reportGuideId: ReportGuideId) => Guid_fetchReportGuideDetails(reportGuideId)

    const onClickGuideParentInfo = (parentInfo: ParentInfo) => {
        Guid_routeToGuideParentPage(parentInfo, () => Rout_back())
    }

    const onReceiveSources = async (sources: AiChatSource[]) => {
        const cachedGuides = await Guid_fetchSyncGuidesByIds(
            logger,
            sources.filter(isAiChatGuideSource).map((entry) => entry.guide.id)
        )

        // Force a guides re-sync to get any recently uploaded guide that's missing in IDB.
        if (cachedGuides.length !== sources.length) {
            await Guides_sync(logger)
        }
    }

    const renderWorkflowGuideExecution = (guide: WorkflowAppGuide) => {
        const { customer } = DS_getData()

        return renderPromise(
            deps,
            'Glbl_ZapBotModalBody.renderWorkflowGuideExecution',
            () => fetchGuideReportId(deps, guide).then(fetchReport),
            (guideReport: Report) => (
                <Flow_renderExecutionPage
                    reportId={guideReport.id}
                    sectionId={guideReport.flow.id}
                    loopPathIds={[]}
                    fetchReport={fetchReport}
                    routeBack={() => null}
                    routeToReturnHere={() => null}
                    routeToExecutionPage={() => null}
                    routeToPostPage={() => null}
                    routeToReportPage={() => null}
                    onSubmit={() => new Promise(() => null)}
                    onCancel={() => new Promise(() => null)}
                    customer={customer.id}
                    showHeader={false}
                    allowFeedbackSubmission={false}
                />
            )
        )
    }

    return (
        <>
            <button
                className="ZapBot_floating-button"
                data-testid="zapbot-floating-button"
                onClick={handleOnClickAiButton}
            >
                <span className="ZapBot_floating-button-icon">
                    <zaptic-icon icon-name="zapbot" />
                </span>
            </button>

            <ZapBotModal
                apiClient={apiClient}
                deps={deps}
                featureFlags={{ aiZapBotFiles: isZapBotFilesEnabled, aiCredits: isAiCreditsEnabled }}
                fetchChatBotAnswer={fetchChatBotResponse}
                fetchFileGuideDetails={fetchFileGuideDetails}
                fetchGuideParentInfo={(guideSource: AiChatGuideSource) => Guid_fetchGuideParentInfo(guideSource.guide)}
                fetchReportGuideDetails={fetchReportGuideDetails}
                fetchAppGuides={(guideSources: AiChatGuideSource[], withParentInfo: boolean) =>
                    fetchAppGuides(
                        guideSources.map(({ guide }) => guide),
                        withParentInfo
                    )
                }
                fetchUserContext={fetchUserContext}
                onClose={handleOnClose}
                onClickGuideParentInfo={onClickGuideParentInfo}
                onClickSource={handleOnClickSource}
                onOpenFile={Guid_downloadFile}
                onReceiveSources={onReceiveSources}
                open={showZapBotModal}
                renderCachedImage={Imag_renderCachedImage}
                allowImageZoom={false}
                renderWorkflowGuideExecution={renderWorkflowGuideExecution}
                beta={true}
            />
        </>
    )
}

function fetchUserContext() {
    return DS_getData().zapBotContext
}

function fetchChatBotResponse(chatId: ChatId, input: string, context: Nullable<AiChatUserContext>) {
    return httpReqWithFetchAPI(route, {
        body: {
            input,
            ...(context && { context }),
        },
        params: { chatId },
    })
}
